import { useState, useEffect, useRef } from 'react';
import { addWebsite } from '../../lib/supabase/websites';
import { supabase } from '../../lib/supabase';
import { Button, IconButton, Input, Dropdown, Popover, SidePanel } from '../../components/ui';

interface AddWebsiteSidepanelProps {
  isOpen: boolean;
  onClose: () => void;
  onWebsiteAdded: () => void;
}

const sectionTitleStyle: React.CSSProperties = {
  marginBottom: 'var(--space-3)',
};

export default function AddWebsiteSidepanel({
  isOpen,
  onClose,
  onWebsiteAdded
}: AddWebsiteSidepanelProps) {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    url: '',
    title: '',
    category: '',
    color: '#d95636',
    dns_host: '',
    nameservers: [] as string[],
    hosting_provider: '',
    hosting_username: '',
    hosting_password: '',
    frontend_platform: '',
    frontend_theme: '',
    search_console_access: '',
    build_status: 'Pending',
    deployment_status: 'Pending',
    indexing_status: 'Not Indexed'
  });
  const [categories, setCategories] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [newNameserver, setNewNameserver] = useState('');
  const categoryBoxRef = useRef<HTMLDivElement>(null);

  // Fetch existing categories
  useEffect(() => {
    const fetchCategories = async () => {
      if (isOpen) {
        try {
          const { data, error } = await supabase
            .from('websites')
            .select('category')
            .not('category', 'is', null);

          if (error) throw error;

          const uniqueCategories = Array.from(new Set(
            (data || []).map((item: { category: string | null }) => item.category).filter(Boolean)
          )) as string[];

          setCategories(uniqueCategories);
        } catch (err) {
          console.error('Error fetching categories:', err);
        }
      }
    };

    fetchCategories();
  }, [isOpen]);

  const filteredCategories = categories.filter(category =>
    category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const setField = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const validateForm = () => {
    // URL is required
    if (!formData.url.trim()) {
      setError('URL is required');
      return false;
    }

    // Basic URL validation
    try {
      // Add protocol if missing
      const urlWithProtocol = formData.url.match(/^https?:\/\//) ? formData.url : `https://${formData.url}`;
      new URL(urlWithProtocol);
    } catch {
      setError('Please enter a valid URL');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await addWebsite(
        formData.url.trim(),
        formData.title.trim() || null,
        {
          category: formData.category.trim() || null,
          color: formData.color,
          dns_host: formData.dns_host || null,
          nameservers: formData.nameservers && formData.nameservers.length > 0
            ? formData.nameservers
            : null,
          hosting_provider: formData.hosting_provider || null,
          hosting_username: formData.hosting_username || null,
          hosting_password: formData.hosting_password || null,
          frontend_platform: formData.frontend_platform || null,
          frontend_theme: formData.frontend_theme || null,
          search_console_access: formData.search_console_access || null,
          build_status: formData.build_status || 'Pending',
          deployment_status: formData.deployment_status || 'Pending',
          indexing_status: formData.indexing_status || 'Not Indexed'
        }
      );

      // Reset form
      setFormData({
        url: '',
        title: '',
        category: '',
        color: '#d95636',
        dns_host: '',
        nameservers: [],
        hosting_provider: '',
        hosting_username: '',
        hosting_password: '',
        frontend_platform: '',
        frontend_theme: '',
        search_console_access: '',
        build_status: 'Pending',
        deployment_status: 'Pending',
        indexing_status: 'Not Indexed'
      });

      onClose();
      onWebsiteAdded();
    } catch (err) {
      console.error('Error adding website:', err);
      setError('Failed to add website. It might already exist in the system.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const addNameserver = () => {
    if (!newNameserver.trim()) return;
    setFormData(prev => ({ ...prev, nameservers: [...prev.nameservers, newNameserver.trim()] }));
    setNewNameserver('');
  };

  const statusOptions = {
    buildStatus: ['Pending', 'In Progress', 'Complete', 'Failed'],
    deploymentStatus: ['Pending', 'In Progress', 'Live', 'Failed'],
    indexingStatus: ['Not Indexed', 'Submitted', 'Partial', 'Indexed', 'Issues']
  };

  const predefinedColors = [
    { label: 'Default (Orange)', value: '#d95636' },
    { label: 'Blue', value: '#4361ee' },
    { label: 'Green', value: '#10b981' },
    { label: 'Purple', value: '#8b5cf6' },
    { label: 'Teal', value: '#14b8a6' },
    { label: 'Pink', value: '#ec4899' },
    { label: "Yellow", value: "#facc15" },
    { label: 'Red', value: '#ef4444' }
  ];

  return (
    <SidePanel
      open={isOpen}
      onClose={onClose}
      width={440}
      eyebrow="Private Network"
      title="Add Website"
      footer={
        <>
          <Button
            icon="plus"
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.url.trim()}
          >
            {isSubmitting ? 'Creating...' : 'Add Website'}
          </Button>
          <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
        </>
      }
    >
      {error && (
        <div
          style={{
            marginBottom: 'var(--space-4)',
            padding: '10px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--status-down-soft)',
            border: '1px solid rgba(229, 72, 77, 0.25)',
            color: 'var(--danger-400)',
            fontSize: 'var(--text-sm)',
          }}
        >
          {error}
        </div>
      )}

      <div className="ck-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
        <Input
          label="URL*"
          icon="globe"
          placeholder="example.com"
          value={formData.url}
          onChange={(e) => setField('url', e.target.value)}
        />

        <Input
          label="Title"
          placeholder="My Website"
          value={formData.title}
          onChange={(e) => setField('title', e.target.value)}
        />

        {/* Category — searchable, allows creating a new one */}
        <div ref={categoryBoxRef} style={{ position: 'relative' }}>
          <Input
            label="Category"
            icon="search"
            placeholder={formData.category || 'Search or enter new category...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => {
              setTimeout(() => setIsSearchFocused(false), 200);
            }}
          />

          <Popover
            open={isSearchFocused}
            anchorEl={categoryBoxRef.current}
            onClose={() => setIsSearchFocused(false)}
            matchWidth
            role="listbox"
            style={{ maxHeight: '240px' }}
          >
              {searchTerm && (
                <button
                  onClick={() => {
                    setField('category', searchTerm);
                    setSearchTerm('');
                  }}
                  className="hover-wash pressable"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    width: '100%',
                    padding: '7px 10px',
                    border: 'none',
                    background: 'transparent',
                    borderRadius: 'var(--radius-sm)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--accent-text)',
                  }}
                >
                  + Add "{searchTerm}"
                </button>
              )}

              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      setField('category', category);
                      setSearchTerm('');
                    }}
                    className="hover-wash pressable"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '7px 10px',
                      border: 'none',
                      background: formData.category === category ? 'var(--surface-active)' : 'transparent',
                      borderRadius: 'var(--radius-sm)',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--text-sm)',
                      color: 'var(--text-body)',
                    }}
                  >
                    {category}
                  </button>
                ))
              ) : (
                !searchTerm && (
                  <div style={{ padding: '7px 10px', fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                    No categories found
                  </div>
                )
              )}
          </Popover>
        </div>

        {/* Node color */}
        <div className="flex items-end" style={{ gap: '10px' }}>
          <div style={{ flex: 1 }}>
            <Dropdown
              label="Node Color"
              options={predefinedColors}
              value={formData.color}
              onChange={(e) => setField('color', e.target.value)}
              placeholder="Color"
            />
          </div>
          <div
            style={{
              width: 'var(--control-md)',
              height: 'var(--control-md)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-hairline)',
              background: formData.color,
              flexShrink: 0,
            }}
          />
        </div>

        {/* Site status */}
        <div style={{ borderTop: '1px solid var(--border-hairline)', paddingTop: 'var(--space-4)' }}>
          <div className="t-eyebrow" style={sectionTitleStyle}>Site Status</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <Dropdown
              label="Build Status"
              options={statusOptions.buildStatus}
              value={formData.build_status}
              onChange={(e) => setField('build_status', e.target.value)}
              placeholder="Select status..."
            />
            <Dropdown
              label="Deployment Status"
              options={statusOptions.deploymentStatus}
              value={formData.deployment_status}
              onChange={(e) => setField('deployment_status', e.target.value)}
              placeholder="Select status..."
            />
            <Dropdown
              label="Indexing Status"
              options={statusOptions.indexingStatus}
              value={formData.indexing_status}
              onChange={(e) => setField('indexing_status', e.target.value)}
              placeholder="Select status..."
            />
          </div>
        </div>

        {/* Hosting */}
        <div style={{ borderTop: '1px solid var(--border-hairline)', paddingTop: 'var(--space-4)' }}>
          <div className="t-eyebrow" style={sectionTitleStyle}>Hosting Details</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <Input
              label="DNS Provider"
              placeholder="Cloudflare, Namecheap, etc."
              value={formData.dns_host}
              onChange={(e) => setField('dns_host', e.target.value)}
            />

            <div>
              <div className="t-eyebrow" style={{ marginBottom: '6px' }}>Nameservers</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {formData.nameservers.map((ns, index) => (
                  <div key={index} className="flex items-center" style={{ gap: '8px' }}>
                    <Input
                      value={ns}
                      style={{ flex: 1 }}
                      onChange={(e) => {
                        const newNameservers = [...formData.nameservers];
                        newNameservers[index] = e.target.value;
                        setFormData({ ...formData, nameservers: newNameservers });
                      }}
                    />
                    <IconButton
                      icon="x"
                      size="sm"
                      variant="outline"
                      label="Remove nameserver"
                      onClick={() => {
                        const newNameservers = [...formData.nameservers];
                        newNameservers.splice(index, 1);
                        setFormData({ ...formData, nameservers: newNameservers });
                      }}
                    />
                  </div>
                ))}

                <div className="flex items-center" style={{ gap: '8px' }}>
                  <Input
                    placeholder="Add a nameserver (e.g., ns1.example.com)"
                    value={newNameserver}
                    style={{ flex: 1 }}
                    onChange={(e) => setNewNameserver(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addNameserver();
                      }
                    }}
                  />
                  <IconButton
                    icon="plus"
                    size="sm"
                    variant="outline"
                    label="Add nameserver"
                    onClick={addNameserver}
                  />
                </div>
              </div>
            </div>

            <Input
              label="Hosting Provider"
              placeholder="Netlify, Vercel, AWS, etc."
              value={formData.hosting_provider}
              onChange={(e) => setField('hosting_provider', e.target.value)}
            />

            <div className="grid grid-cols-2" style={{ gap: 'var(--space-4)' }}>
              <Input
                label="Hosting Username"
                value={formData.hosting_username}
                onChange={(e) => setField('hosting_username', e.target.value)}
              />
              <Input
                label="Hosting Password"
                type="password"
                value={formData.hosting_password}
                onChange={(e) => setField('hosting_password', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Frontend */}
        <div style={{ borderTop: '1px solid var(--border-hairline)', paddingTop: 'var(--space-4)' }}>
          <div className="t-eyebrow" style={sectionTitleStyle}>Frontend Details</div>
          <div className="grid grid-cols-2" style={{ gap: 'var(--space-4)' }}>
            <Input
              label="Frontend Platform"
              placeholder="React, Vue, WordPress, etc."
              value={formData.frontend_platform}
              onChange={(e) => setField('frontend_platform', e.target.value)}
            />
            <Input
              label="Frontend Theme"
              placeholder="Theme name or custom"
              value={formData.frontend_theme}
              onChange={(e) => setField('frontend_theme', e.target.value)}
            />
          </div>
        </div>

        <Input
          label="Search Console Access"
          placeholder="Email address or access notes"
          value={formData.search_console_access}
          onChange={(e) => setField('search_console_access', e.target.value)}
        />
      </div>
    </SidePanel>
  );
}
